import type { EmptyObject } from 'type-fest'

import type {
  ActivityId,
  BidId,
  BillingProfileId,
  DeliverableId,
  FileId,
  NotificationId,
  NotificationUrlSlug,
  OrganizationId,
  PackageId,
  ProjectId,
  ProjectServiceId,
  ProjectSurveyType,
  TeamId,
  UserId,
} from '@mntn-dev/domain-types'

import type {
  AccountRouteAttributes,
  BreadCrumbRouteAttributes,
  EmptyQueryParams,
  EmptyRouteParams,
} from './attributes/types.ts'
import { register } from './register.ts'
import type { DashboardSortBy, SortDirection } from './types/sort-types.ts'
import type { DashboardPageTab, ProjectPageTab } from './types/tab-types.ts'

const routes = [
  // Unprotected

  register('/api/trpc/public').match({ wildcard: true }).toRoute({}),

  register('/api/trpc/session.unauthenticatedSession').toRoute({}),

  register('/api/trpc/session.authenticatedSession').toRoute({}),

  register('/api/auth/sign-out').toRoute<
    EmptyRouteParams,
    { redirect_url?: string }
  >({}),

  register('/api/realtime').toRoute({}),

  register('/api/auth/principal').toRoute({}),

  register('/api/auth/clear').toRoute({}),

  register('/api/auth').match({ wildcard: true }).toRoute({}),

  register('/sign-in')
    .match({ wildcard: true })
    .toRoute<EmptyRouteParams, { redirect_url?: string }>({}),

  register('/sign-up').toRoute<EmptyRouteParams, { returnTo?: string }>({}),

  register('/sign-out').toRoute<EmptyRouteParams, { redirect_url?: string }>(
    {}
  ),

  register('/welcome').toRoute<EmptyRouteParams, { returnTo?: string }>({}),

  register('/disabled').toRoute({}),

  // Protected

  register('/test/blank').protected().toRoute({}),

  register('/test/error').protected().toRoute({}),

  register('/api/trpc').match({ wildcard: true }).protected().toRoute({}),

  register('/auth/lab').protected().toRoute({}),

  register('/auth/callback')
    .protected()
    .toRoute<EmptyObject, { redirect_url?: string; ticket?: string }>({}),

  register('/')
    .protected()
    .match({ exact: true /** Root route should not be a catch-all route */ })
    .toRoute({}),

  register('/account')
    .protected()
    .toRoute<EmptyRouteParams, EmptyQueryParams, AccountRouteAttributes>({
      name: 'account',
    }),

  register('/account/teams')
    .protected()
    .toRoute<EmptyRouteParams, { teamId: TeamId }, AccountRouteAttributes>({
      name: 'teams',
      tab: 'teams',
    }),

  register('/account/profile')
    .protected()
    .toRoute<EmptyRouteParams, EmptyQueryParams, AccountRouteAttributes>({
      name: 'profile',
      tab: 'profile',
    }),

  register('/account/organizations')
    .protected()
    .authorization(({ permissions }) =>
      permissions.includes('customer-organization:administer')
    )
    .toRoute<EmptyRouteParams, EmptyQueryParams, AccountRouteAttributes>({
      tab: 'organizations',
      name: 'organizations',
    }),

  register('/account/organizations/:organizationId')
    .protected()
    .authorization(
      ({ multiTeam, permissions }) =>
        multiTeam &&
        (permissions.includes('own-organization:administer') ||
          permissions.includes('customer-organization:administer'))
    )
    .toRoute<
      { organizationId: OrganizationId },
      EmptyQueryParams,
      AccountRouteAttributes
    >({
      tab: ['organization', 'organizations'],
      name: 'organization',
    }),

  register('/account/organizations/:organizationId/billing')
    .protected()
    .authorization(
      ({ permissions }) =>
        permissions.includes('own-organization:administer') ||
        permissions.includes('customer-organization:administer')
    )
    .toRoute<
      { organizationId: OrganizationId },
      EmptyQueryParams,
      AccountRouteAttributes
    >({ tab: 'billing', name: 'billing' }),

  register('/account/organizations/:organizationId/billing/transfer-credits')
    .protected()
    .authorization(
      ({ permissions, flags }) =>
        flags.transferCredits &&
        (permissions.includes('customer-organization:administer') ||
          permissions.includes('own-organization:administer'))
    )
    .toRoute<
      { organizationId: OrganizationId },
      EmptyQueryParams,
      AccountRouteAttributes
    >({
      tab: 'billing',
      name: 'transfer-credits',
    }),

  register(
    '/account/organizations/:organizationId/billing/profiles/:billingProfileId'
  )
    .protected()
    .authorization(
      ({ permissions }) =>
        permissions.includes('own-organization:administer') ||
        permissions.includes('customer-organization:administer')
    )
    .toRoute<
      { organizationId: OrganizationId; billingProfileId: BillingProfileId },
      EmptyQueryParams,
      AccountRouteAttributes
    >({ tab: ['organization', 'billing'], name: 'billingProfile' }),

  register(
    '/account/organizations/:organizationId/billing/profiles/:billingProfileId/credit-cards'
  )
    .protected()
    .authorization(
      ({ permissions }) =>
        permissions.includes('own-organization:administer') ||
        permissions.includes('customer-organization:administer')
    )
    .toRoute<
      { organizationId: OrganizationId; billingProfileId: BillingProfileId },
      EmptyQueryParams,
      AccountRouteAttributes
    >({ tab: 'billing', name: 'creditCards' }),

  register(
    '/account/organizations/:organizationId/billing/profiles/:billingProfileId/credit-cards/add-card'
  )
    .protected()
    .authorization(
      ({ permissions }) =>
        permissions.includes('own-organization:administer') ||
        permissions.includes('customer-organization:administer')
    )
    .toRoute<
      { organizationId: OrganizationId; billingProfileId: BillingProfileId },
      EmptyQueryParams,
      AccountRouteAttributes
    >({ tab: 'billing', name: 'addCreditCard' }),

  register('/account/organizations/:organizationId/billing/methods')
    .protected()
    .authorization(
      ({ permissions }) =>
        permissions.includes('own-organization:administer') ||
        permissions.includes('customer-organization:administer')
    )
    .toRoute<
      { organizationId: OrganizationId },
      EmptyQueryParams,
      AccountRouteAttributes
    >({ tab: 'billing', name: 'paymentMethods' }),

  register('/account/organizations/:organizationId/billing/methods/add-method')
    .protected()
    .authorization(
      ({ permissions }) =>
        permissions.includes('own-organization:administer') ||
        permissions.includes('customer-organization:administer')
    )
    .toRoute<
      { organizationId: OrganizationId },
      EmptyQueryParams,
      AccountRouteAttributes
    >({ tab: 'billing', name: 'addPaymentMethod' }),

  register('/account/organizations/:organizationId/billing/profile/new')
    .protected()
    .authorization(
      ({ permissions }) =>
        permissions.includes('own-organization:administer') ||
        permissions.includes('customer-organization:administer')
    )
    .toRoute<
      { organizationId: OrganizationId },
      EmptyObject,
      AccountRouteAttributes
    >({ tab: 'billing', name: 'newBillingProfile' }),

  register('/account/organizations/:organizationId/payments')
    .protected()
    .authorization(
      ({ permissions }) =>
        permissions.includes('own-organization:administer') ||
        permissions.includes('customer-organization:administer')
    )
    .toRoute<
      { organizationId: OrganizationId },
      EmptyQueryParams,
      AccountRouteAttributes
    >({ tab: 'payments', name: 'payments' }),

  register('/account/organizations/:organizationId/payments/setup')
    .protected()
    .authorization(
      ({ permissions }) =>
        permissions.includes('own-organization:administer') ||
        permissions.includes('customer-organization:administer')
    )
    .toRoute<
      { organizationId: OrganizationId },
      EmptyObject,
      AccountRouteAttributes
    >({ tab: 'payments', name: 'paymentsSetup' }),

  register('/account/organizations/:organizationId/teams')
    .protected()
    .authorization(({ multiTeam }) => multiTeam)
    .toRoute<
      { organizationId: OrganizationId },
      { teamId: TeamId },
      AccountRouteAttributes
    >({
      name: 'teams',
      tab: 'teams',
    }),

  register('/account/organizations/:organizationId/teams/:teamId')
    .protected()
    .authorization(({ multiTeam }) => !multiTeam)
    .toRoute<
      { organizationId: OrganizationId; teamId: TeamId },
      EmptyQueryParams,
      AccountRouteAttributes
    >({
      name: 'team',
      tab: ['team', 'teams'],
    }),

  register('/account/organizations/:organizationId/users')
    .protected()
    .authorization(
      ({ permissions }) =>
        permissions.includes('own-organization:administer') ||
        permissions.includes('customer-organization:administer')
    )
    .toRoute<
      { organizationId: OrganizationId },
      EmptyQueryParams,
      AccountRouteAttributes
    >({ name: 'users', tab: 'users' }),

  register('/account/organizations/:organizationId/users/:userId')
    .protected()
    .authorization(
      ({ permissions }) =>
        permissions.includes('own-organization:administer') ||
        permissions.includes('customer-organization:administer')
    )
    .toRoute<
      { organizationId: OrganizationId; userId: UserId },
      EmptyQueryParams,
      AccountRouteAttributes
    >({ name: 'user', tab: 'users' }),

  register('/dashboard')
    .protected([['project:view']])
    .toRoute<
      EmptyRouteParams,
      {
        search?: string
        sortBy?: DashboardSortBy
        sortDir?: SortDirection
        tab?: DashboardPageTab
        organizationId?: OrganizationId
      }
    >({}),

  register('/notifications/:notificationId').protected().toRoute<{
    notificationId: NotificationId
  }>({}),

  register('/notifications/:notificationId/:slug').protected().toRoute<{
    notificationId: NotificationId
    slug: NotificationUrlSlug
  }>({}),

  register('/packages').protected().toRoute({}),

  register('/packages/:packageId')
    .protected()
    .toRoute<{ packageId: PackageId }>({}),

  register('/packages/services').protected().toRoute({}),

  register('/projects').protected().toRoute({}),

  register('/projects/:projectId')
    .protected()
    .toRoute<
      { projectId: ProjectId },
      { tab?: ProjectPageTab; activityId?: ActivityId }
    >({}),

  register('/projects/:projectId/bid/:bidId')
    .protected()
    .toRoute<{ projectId: ProjectId; bidId: BidId }>({}),

  register('/projects/:projectId/bid/:bidId/edit')
    .protected()
    .toRoute<{ projectId: ProjectId; bidId: BidId }>({}),

  register('/projects/:projectId/services/:projectServiceId')
    .protected()
    .toRoute<{
      projectId: ProjectId
      projectServiceId: ProjectServiceId
    }>({}),

  register('/projects/:projectId/services/:projectServiceId/review')
    .protected()
    .toRoute<{
      projectId: ProjectId
      projectServiceId: ProjectServiceId
    }>({}),

  register('/projects/:projectId/match')
    .protected()
    .toRoute<{ projectId: ProjectId }>({}),

  register('/projects/:projectId/match/maker/:userId').protected().toRoute<{
    projectId: ProjectId
    userId: UserId
  }>({}),

  register('/projects/:projectId/browse').protected().toRoute<{
    projectId: ProjectId
  }>({}),

  register('/projects/:projectId/change-order').protected().toRoute<
    {
      projectId: ProjectId
    },
    EmptyQueryParams,
    BreadCrumbRouteAttributes
  >({
    name: 'change-order',
  }),

  register('/projects/:projectId/post-production-review').protected().toRoute<{
    projectId: ProjectId
  }>({}),

  register('/projects/:projectId/post-production-review/:deliverableId')
    .protected()
    .toRoute<{
      projectId: ProjectId
      deliverableId?: DeliverableId
    }>({}),

  register('/projects/:projectId/files').protected().toRoute<{
    projectId: ProjectId
  }>({}),

  register('/projects/:projectId/files/:fileId').protected().toRoute<{
    projectId: ProjectId
    fileId: FileId
  }>({}),

  register('/projects/:projectId/survey').protected().toRoute<
    {
      projectId: ProjectId
    },
    { type: ProjectSurveyType }
  >({}),

  register('/projects/new').protected().toRoute({}),

  register('/invoices/:invoiceId/preview')
    .protected()
    .toRoute<{ invoiceId: string }>({}),

  // Testing

  register('/foo')
    .protected([['project:view']])
    .toRoute<EmptyRouteParams, { baz: string }>({}),

  register('/foo/:fooId').toRoute<{ fooId: string }, { baz: string }>({}),

  register('/foo/:fooId/bar/:barId').toRoute<
    { fooId: string; barId: string },
    { baz: string; qux: string; cog?: string }
  >({}),

  register('/foo/exclude').protected().match({ exclude: true }).toRoute({}),
]

export default routes
